import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import goalViaLogo from "@/assets/goalvia-logo.png";
import { userLsKey, clearUserScopedStorage } from "@/lib/userScopedStorage";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { getQueryFn } from "@/lib/queryClient";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Target, 
  Settings,
  CheckSquare,
  LogOut,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ListTodo,
  GripVertical,
  Search,
  Shield,
  StickyNote,
  Film,
  Folder,
  HardDrive,
  PanelLeftClose,
  PanelLeft,
  ChevronsUpDown,
  ChevronsDownUp,
  Plus,
  CalendarPlus
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { useCalendarSourceFilter } from "@/hooks/useCalendarSourceFilter";
import { useNavigationOrder, type NavigationItem } from "@/hooks/useNavigationOrder";
import { usePlanContext } from "@/contexts/PlanContext";
import { useSidebarCollapsed } from "@/hooks/useSidebarCollapsed";
import type { ResellerConfig } from "@shared/schema";
import { menuItemDefinitions, menuItemFeatureKeyOverrides, menuItemCapabilityMap, assistantCanAccessMenuItem, type AssistantCapability } from "@/lib/menu-items";
import TourLauncher from "@/components/tour-launcher";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import NotificationBell from "@/components/notification-bell";
import { TimeClockButton } from "@/components/time-clock-button";
import { MessageBadge } from "@/components/messaging/message-badge";
import { AdditionalCalendarsContent } from "@/components/internal-calendars-panel";

// ── Calendar Filter Panel ──────────────────────────────────────────────────

// Validated (not merely asserted) shape of the GET /api/calendar/connections
// response. Only the fields this panel consumes are declared; unknown extra
// fields are stripped by Zod. Shape drift now fails the query loudly instead
// of silently rendering an empty or broken feed list.
const CalendarConnectionRowSchema = z.object({
  id: z.string(),
  provider: z.string(),
  displayName: z.string().nullable().optional(),
  externalCalendars: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        color: z.string().nullable().optional(),
        isVisible: z.boolean().nullable().optional(),
      })
    )
    .optional(),
});
const CalendarConnectionsResponseSchema = z.array(CalendarConnectionRowSchema);

interface GcalAccount {
  connectionId: string;
  email?: string | null;
  calendars: Array<{ id: string; name: string; color: string | null; isVisible: boolean }>;
}

function GcalAccountPanel({
  account,
  isVisible,
  toggleSource,
  setSourcesVisibility,
}: {
  account: GcalAccount;
  isVisible: (id: string) => boolean;
  toggleSource: (id: string) => void;
  setSourcesVisibility: (ids: string[], visible: boolean) => void;
}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [open, setOpen] = useState(true);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(userLsKey(`cal-filter-gcal-${account.connectionId}`, userId));
      if (saved !== null) setOpen(saved !== 'false');
      if (userId) localStorage.removeItem(`cal-filter-gcal-${account.connectionId}`);
    } catch {}
  }, [userId, account.connectionId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(userLsKey(`cal-filter-gcal-${account.connectionId}`, userId), String(next));
  };
  const calendarKeys = account.calendars.map(calendar => `ext:${calendar.id}`);
  const allVisible = calendarKeys.every(isVisible);

  return (
    <div>
      <div className="flex items-center gap-1">
        <button className="flex items-center min-w-0 flex-1 py-1 text-xs hover:text-foreground text-muted-foreground transition-colors" onClick={toggle}>
          {open ? <ChevronDown className="w-3 h-3 mr-1 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 mr-1 flex-shrink-0" />}
          <span className="font-medium text-foreground/70 truncate">{account.email ?? "Google Calendar"}</span>
        </button>
        {account.calendars.length > 0 && (
          <button
            type="button"
            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10"
            onClick={() => setSourcesVisibility(calendarKeys, !allVisible)}
            aria-label={`${allVisible ? "Hide" : "Show"} all Google calendars for ${account.email ?? "this account"}`}
            data-testid={`button-${allVisible ? "hide" : "show"}-all-google-calendars-${account.connectionId}`}
          >
            {allVisible ? "Hide all" : "Show all"}
          </button>
        )}
      </div>
      {open && (
        <div className="ml-4 space-y-0.5">
          {account.calendars.map(cal => {
            const key = `ext:${cal.id}`;
            const visible = isVisible(key);
            return (
              <button key={cal.id} className="flex items-center gap-2 w-full py-0.5 text-sm" onClick={() => toggleSource(key)} title={visible ? `Hide ${cal.name}` : `Show ${cal.name}`}>
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: cal.color ?? '#3b82f6', opacity: visible ? 1 : 0.25 }} />
                <span className={`text-left truncate ${visible ? 'text-foreground' : 'text-muted-foreground/60 line-through'}`}>{cal.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const BUILT_IN_SOURCES = [
  { id: "type:event",    label: "Events",           color: "#6b7280" },
  { id: "type:goal",     label: "Goals",            color: "#3b82f6" },
  { id: "type:task",     label: "Tasks",            color: "#8b5cf6" },
  { id: "type:reminder", label: "Reminders",       color: "#f59e0b" },
];

export function CalendarSidebarPanel({
  sidebarCollapsed = false,
  isAuthenticated,
  instance = "desktop",
}: {
  sidebarCollapsed?: boolean;
  isAuthenticated: boolean;
  instance?: "desktop" | "mobile";
}) {
  const [location] = useLocation();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { isVisible, toggleSource, setSourcesVisibility } = useCalendarSourceFilter();

  const [panelOpen, setPanelOpen] = useState(true);
  const [myCalOpen, setMyCalOpen] = useState(true);
  const [iCalOpen, setICalOpen]   = useState(true);

  useEffect(() => {
    try {
      const p = localStorage.getItem(userLsKey('cal-filter-panel', userId));
      if (p !== null) setPanelOpen(p !== 'false');
      const m = localStorage.getItem(userLsKey('cal-filter-mycal', userId));
      if (m !== null) setMyCalOpen(m !== 'false');
      const i = localStorage.getItem(userLsKey('cal-filter-ical', userId));
      if (i !== null) setICalOpen(i !== 'false');
      if (userId) {
        localStorage.removeItem('cal-filter-panel');
        localStorage.removeItem('cal-filter-mycal');
        localStorage.removeItem('cal-filter-ical');
      }
    } catch {}
  }, [userId]);

  const { data: gcalData } = useQuery<{ connections: GcalAccount[] }>({
    queryKey: ["/api/google-calendar/connection"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && location === "/calendar",
  });

  const { data: connections = [] } = useQuery({
    queryKey: ["/api/calendar/connections"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && location === "/calendar",
    select: (data: unknown) =>
      data === null ? [] : CalendarConnectionsResponseSchema.parse(data),
  });

  if (location.replace(/\/$/, '') !== "/calendar" || sidebarCollapsed) return null;

  const gcalAccounts = gcalData?.connections ?? [];
  const iCalFeeds = connections.filter((c) => c.provider === 'ical');

  return (
    <div
      className={
        instance === "desktop"
          ? "flex-1 basis-0 min-h-0 border-t border-border px-3 pt-2 pb-3 overflow-y-auto"
          : "flex-shrink-0 border-t border-border px-3 pt-2 pb-3 max-h-[55vh] overflow-y-auto"
      }
      data-testid={`calendar-sidebar-panel-${instance}`}
    >
      <button
        className="flex items-center w-full py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
        onClick={() => { const n = !panelOpen; setPanelOpen(n); localStorage.setItem(userLsKey('cal-filter-panel', userId), String(n)); }}
      >
        {panelOpen ? <ChevronDown className="w-3 h-3 mr-1.5 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 mr-1.5 flex-shrink-0" />}
        Calendars
      </button>

      {panelOpen && (
        <div className="space-y-0.5 mt-0.5">
          {/* Google Calendar accounts are always the first calendar section. */}
          {gcalAccounts.map(account => (
            <GcalAccountPanel
              key={account.connectionId}
              account={account}
              isVisible={isVisible}
              toggleSource={toggleSource}
              setSourcesVisibility={setSourcesVisibility}
            />
          ))}

          {/* Current Calendar – built-in event types */}
          <div>
            <button
              className="flex items-center w-full py-1 text-xs hover:text-foreground text-muted-foreground transition-colors"
              onClick={() => { const n = !myCalOpen; setMyCalOpen(n); localStorage.setItem(userLsKey('cal-filter-mycal', userId), String(n)); }}
            >
              {myCalOpen ? <ChevronDown className="w-3 h-3 mr-1 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 mr-1 flex-shrink-0" />}
              <span className="font-medium text-foreground/70">Current Calendar</span>
            </button>
            {myCalOpen && (
              <div className="ml-4 space-y-0.5">
                {BUILT_IN_SOURCES.map(src => {
                  const visible = isVisible(src.id);
                  return (
                    <button key={src.id} className="flex items-center gap-2 w-full py-0.5 text-sm" onClick={() => toggleSource(src.id)} title={visible ? `Hide ${src.label}` : `Show ${src.label}`}>
                      <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: src.color, opacity: visible ? 1 : 0.25 }} />
                      <span className={`text-left truncate ${visible ? 'text-foreground' : 'text-muted-foreground/60 line-through'}`}>{src.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-border/60 mt-2 pt-3">
            <AdditionalCalendarsContent instance={instance} />
          </div>

          {/* Existing read-only iCal feeds remain available after calendar sections. */}
          {iCalFeeds.length > 0 && (
            <div>
              <button
                className="flex items-center w-full py-1 text-xs hover:text-foreground text-muted-foreground transition-colors"
                onClick={() => { const n = !iCalOpen; setICalOpen(n); localStorage.setItem(userLsKey('cal-filter-ical', userId), String(n)); }}
              >
                {iCalOpen ? <ChevronDown className="w-3 h-3 mr-1 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 mr-1 flex-shrink-0" />}
                <span className="font-medium text-foreground/70">Connected Feeds</span>
              </button>
              {iCalOpen && (
                <div className="ml-4 space-y-0.5">
                  {iCalFeeds.map((feed) => {
                    const cals = (feed.externalCalendars ?? []).filter((c) => c.isVisible !== false);
                    return cals.map((cal) => {
                      const key = `ext:${cal.id}`;
                      const visible = isVisible(key);
                      const label = feed.displayName || cal.name;
                      return (
                        <button key={cal.id} className="flex items-center gap-2 w-full py-0.5 text-sm" onClick={() => toggleSource(key)} title={visible ? `Hide ${label}` : `Show ${label}`}>
                          <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: cal.color ?? '#6b7280', opacity: visible ? 1 : 0.25 }} />
                          <span className={`text-left truncate ${visible ? 'text-foreground' : 'text-muted-foreground/60 line-through'}`}>{label}</span>
                        </button>
                      );
                    });
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── End Calendar Filter Panel ──────────────────────────────────────────────

interface SortableNavItemProps {
  item: NavigationItem;
  isOpen?: boolean;
  onToggle?: () => void;
  sidebarCollapsed?: boolean;
  isNestTarget?: boolean;
  isHidden?: boolean;
  onHoverEnter?: (sectionId: string, element: Element) => void;
  onHoverLeave?: (sectionId: string) => void;
  /** undefined = regular user (no assistant treatment)
   *  true  = assistant capability granted → dark green
   *  false = assistant capability not granted → grayed out + non-clickable */
  assistantAccessible?: boolean;
}

function SortableNavItem({ item, isOpen, onToggle, sidebarCollapsed, isNestTarget, isHidden, onHoverEnter, onHoverLeave, assistantAccessible }: SortableNavItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Hide items whose parent collapsible is closed
  if (isHidden) {
    return null;
  }

  // Assistant capability styling
  const assistantDisabled = assistantAccessible === false;
  const assistantEnabled = assistantAccessible === true;
  // Applied to the outermost wrapper so the whole item is non-interactive when disabled
  const disabledClass = assistantDisabled ? "opacity-40 pointer-events-none select-none" : "";
  // Text/hover classes for the inactive (non-current-page) state
  const inactiveClass = assistantEnabled
    ? "text-emerald-700 dark:text-emerald-500 hover:text-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950"
    : "text-muted-foreground hover:text-foreground hover:bg-muted";

  const Icon = item.icon;
  const depth = item.depth || 0;
  const indentPadding = sidebarCollapsed ? 0 : depth * 16;

  // Determine which section this item belongs to for hover-expand
  // Collapsible items own themselves; children report their parent section
  const hoverSectionId = item.isCollapsible
    ? item.id
    : item.parentId ?? null;

  const hoverProps = hoverSectionId
    ? {
        onMouseEnter: (e: React.MouseEvent) => onHoverEnter?.(hoverSectionId, e.currentTarget as Element),
        onMouseLeave: () => onHoverLeave?.(hoverSectionId),
      }
    : {};

  // Collapsed sidebar - show only icons with tooltips
  if (sidebarCollapsed) {
    if (item.isCollapsible) {
      // "#" is a placeholder — treat as no-href so it gets the softer active style
      if (item.href && item.href !== "#") {
        return (
          <div ref={setNodeRef} style={style} className={`group ${disabledClass}`} {...hoverProps}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href={item.href}>
                  <Button
                    variant={item.current ? "default" : "ghost"}
                    size="icon"
                    className={`w-full ${
                      item.current 
                        ? "bg-primary text-primary-foreground" 
                        : inactiveClass
                    }`}
                    data-testid={`link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">
                {item.name}
              </TooltipContent>
            </Tooltip>
          </div>
        );
      }
      
      return (
        <div ref={setNodeRef} style={style} className={`relative group ${disabledClass}`} {...hoverProps}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggle}
                className={`w-full ${
                  item.current 
                    ? "text-primary bg-primary/10" 
                    : inactiveClass
                }`}
                data-testid={`button-${item.name.toLowerCase().replace(/\s+/g, '-')}-toggle`}
              >
                <Icon className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {item.name}
            </TooltipContent>
          </Tooltip>
        </div>
      );
    }

    return (
      <div ref={setNodeRef} style={style} className={`group ${disabledClass}`} {...hoverProps}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link href={item.href}>
              <Button
                variant={item.current ? "default" : "ghost"}
                size="icon"
                className={`w-full ${
                  item.current 
                    ? "bg-primary text-primary-foreground" 
                    : inactiveClass
                }`}
                data-testid={`link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
              </Button>
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">
            {item.name}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  // Expanded sidebar - collapsible items (children are rendered as separate items in the navigation array)
  if (item.isCollapsible) {
    // "#" is a placeholder href used for sections that are not real pages — treat as no-href
    // so they get the softer text-primary highlight instead of the full bg-primary one
    if (item.href && item.href !== "#") {
      return (
        <div ref={setNodeRef} style={{ ...style, paddingLeft: indentPadding }} className={`relative group ${isNestTarget ? 'bg-primary/20 rounded-md' : ''} ${disabledClass}`} data-nav-id={item.id} {...hoverProps}>
          <Link href={item.href} className="block w-full">
            <Button
              variant={item.current ? "default" : "ghost"}
              className={`w-full justify-between ${
                item.current 
                  ? "bg-primary text-primary-foreground" 
                  : inactiveClass
              }`}
              data-testid={`link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="flex items-center">
                <Icon className="w-4 h-4 mr-3" />
                {item.name}
              </span>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggle?.();
                }}
                className="ml-auto flex items-center"
                data-testid={`button-${item.name.toLowerCase().replace(/\s+/g, '-')}-toggle`}
              >
                {isOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </span>
            </Button>
          </Link>
          <button
            {...attributes}
            {...listeners}
            className="absolute left-0 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
            aria-label={`Drag to reorder ${item.name}`}
            data-testid={`drag-handle-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      );
    }
    
    // Regular collapsible without href - click anywhere toggles
    return (
      <div ref={setNodeRef} style={{ ...style, paddingLeft: indentPadding }} className={`relative group ${isNestTarget ? 'bg-primary/20 rounded-md' : ''} ${disabledClass}`} data-nav-id={item.id} {...hoverProps}>
        <Button
          variant="ghost"
          onClick={onToggle}
          className={`w-full justify-between ${
            item.current 
              ? "text-primary" 
              : inactiveClass
          }`}
          data-testid={`button-${item.name.toLowerCase().replace(/\s+/g, '-')}-toggle`}
        >
          <span className="flex items-center">
            <Icon className="w-4 h-4 mr-3" />
            {item.name}
          </span>
          <span className="ml-auto">
            {isOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </span>
        </Button>
        <button
          {...attributes}
          {...listeners}
          className="absolute left-0 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
          aria-label={`Drag to reorder ${item.name}`}
          data-testid={`drag-handle-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={setNodeRef} 
      style={{ ...style, paddingLeft: indentPadding }} 
      className={`group flex items-center gap-1 ${isNestTarget ? 'bg-primary/20 rounded-md' : ''} ${disabledClass}`}
      data-nav-id={item.id}
      {...hoverProps}
    >
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        aria-label={`Drag to reorder ${item.name}`}
        data-testid={`drag-handle-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </button>
      <Link href={item.href} className="flex-grow min-w-0">
        <Button
          variant={item.current ? "default" : "ghost"}
          className={`w-full justify-start ${
            item.current 
              ? "bg-primary text-primary-foreground" 
              : inactiveClass
          }`}
          data-testid={`link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Icon className="w-4 h-4 mr-3" />
          {item.name}
        </Button>
      </Link>
      {item.showTourLauncher && (
        <span className="flex-shrink-0">
          <TourLauncher variant="inline" />
        </span>
      )}
    </div>
  );
}

export default function Sidebar() {
  const [location] = useLocation();
  const { user, tenant, isAdmin, isMasterAdmin, isAuthenticated } = useAuth();
  const { isPlatformOwner, hasPermission } = usePlatformPermissions();
  
  // Initialize collapsible states from user-scoped localStorage
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aiCoachOpen, setAiCoachOpen] = useState(false);
  const [mindOpen, setMindOpen] = useState(true);
  const [achieveOpen, setAchieveOpen] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(true);
  const [organizeOpen, setOrganizeOpen] = useState(true);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);

  // Load all collapsible states from user-scoped keys once userId is known.
  useEffect(() => {
    const uid = user?.id ?? null;
    const read = (key: string, def: boolean) => {
      try {
        const saved = localStorage.getItem(userLsKey(key, uid));
        if (saved !== null) return saved === 'true';
        // fall through to default
      } catch {}
      return def;
    };
    setSettingsOpen(read('sidebar-settings-open', false));
    setAiCoachOpen(read('sidebar-aicoach-open', false));
    setMindOpen(read('sidebar-mind-open', true));
    setAchieveOpen(read('sidebar-achieve-open', true));
    setReviewOpen(read('sidebar-review-open', true));
    setOrganizeOpen(read('sidebar-organize-open', true));
    setMediaOpen(read('sidebar-media-open', false));
    setHealthOpen(read('sidebar-health-open', false));
    setCrmOpen(read('sidebar-crm-open', false));
    // Remove unscoped legacy keys when user is known.
    if (uid) {
      ['sidebar-settings-open','sidebar-aicoach-open','sidebar-mind-open',
       'sidebar-achieve-open','sidebar-review-open','sidebar-organize-open',
       'sidebar-media-open','sidebar-health-open','sidebar-crm-open'].forEach(k => {
        try { localStorage.removeItem(k); } catch {}
      });
    }
  }, [user?.id]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [nestTargetId, setNestTargetId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | 'nest' | null>(null);
  const mousePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Track mouse position during drag
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePositionRef.current = { x: e.clientX, y: e.clientY };
    };
    
    if (activeId) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
    return undefined;
  }, [activeId]);
  
  // Persist collapsible states to localStorage
  const scrollGroupIntoView = (groupId: string) => {
    setTimeout(() => {
      const groupEl = document.querySelector(`[data-nav-id="${groupId}"]`);
      if (!groupEl) return;
      const scrollContainer = groupEl.closest('.overflow-y-auto');
      if (!scrollContainer) {
        groupEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }
      const containerRect = scrollContainer.getBoundingClientRect();
      const groupRect = groupEl.getBoundingClientRect();
      const groupBottom = groupRect.bottom;
      const visibleBottom = containerRect.bottom;
      if (groupBottom > visibleBottom - 80) {
        const scrollAmount = groupBottom - visibleBottom + 120;
        scrollContainer.scrollBy({ top: scrollAmount, behavior: 'smooth' });
      }
    }, 100);
  };

  const uid = user?.id ?? null;
  const handleSettingsToggle = (open: boolean) => {
    setSettingsOpen(open);
    localStorage.setItem(userLsKey('sidebar-settings-open', uid), String(open));
    if (open) scrollGroupIntoView('settings');
  };
  const handleAiCoachToggle = (open: boolean) => {
    setAiCoachOpen(open);
    localStorage.setItem(userLsKey('sidebar-aicoach-open', uid), String(open));
    if (open) scrollGroupIntoView('ai-coach');
  };
  const handleMindToggle = (open: boolean) => {
    setMindOpen(open);
    localStorage.setItem(userLsKey('sidebar-mind-open', uid), String(open));
    if (open) scrollGroupIntoView('mind');
  };
  const handleAchieveToggle = (open: boolean) => {
    setAchieveOpen(open);
    localStorage.setItem(userLsKey('sidebar-achieve-open', uid), String(open));
    if (open) scrollGroupIntoView('achieve');
  };
  const handleReviewToggle = (open: boolean) => {
    setReviewOpen(open);
    localStorage.setItem(userLsKey('sidebar-review-open', uid), String(open));
    if (open) scrollGroupIntoView('review');
  };
  const handleOrganizeToggle = (open: boolean) => {
    setOrganizeOpen(open);
    localStorage.setItem(userLsKey('sidebar-organize-open', uid), String(open));
    if (open) scrollGroupIntoView('organize');
  };
  const handleMediaToggle = (open: boolean) => {
    setMediaOpen(open);
    localStorage.setItem(userLsKey('sidebar-media-open', uid), String(open));
    if (open) scrollGroupIntoView('media');
  };
  const handleHealthToggle = (open: boolean) => {
    setHealthOpen(open);
    localStorage.setItem(userLsKey('sidebar-health-open', uid), String(open));
    if (open) scrollGroupIntoView('health');
  };
  const handleCrmToggle = (open: boolean) => {
    setCrmOpen(open);
    localStorage.setItem(userLsKey('sidebar-crm-open', uid), String(open));
    if (open) scrollGroupIntoView('crm');
  };

  // ── Hover flyout ──────────────────────────────────────────────────────────
  // Hovering a collapsible section shows a panel that pops out to the RIGHT of
  // the sidebar instead of expanding the section inline. A 300 ms grace period
  // lets the mouse transit from the sidebar item to the flyout without it
  // snapping shut mid-way.
  const [flyoutSection, setFlyoutSection] = useState<string | null>(null);
  const [flyoutY, setFlyoutY] = useState<number>(0);
  const flyoutCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSectionHoverEnter = useCallback((sectionId: string, element: Element) => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
      flyoutCloseTimerRef.current = null;
    }
    const rect = element.getBoundingClientRect();
    setFlyoutSection(sectionId);
    setFlyoutY(rect.top);
  }, []);

  const handleSectionHoverLeave = useCallback((_sectionId: string) => {
    flyoutCloseTimerRef.current = setTimeout(() => {
      flyoutCloseTimerRef.current = null;
      setFlyoutSection(null);
    }, 300);
  }, []);

  const handleFlyoutMouseEnter = useCallback(() => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
      flyoutCloseTimerRef.current = null;
    }
  }, []);

  const handleFlyoutMouseLeave = useCallback(() => {
    flyoutCloseTimerRef.current = setTimeout(() => {
      flyoutCloseTimerRef.current = null;
      setFlyoutSection(null);
    }, 300);
  }, []);

  useEffect(() => {
    return () => { if (flyoutCloseTimerRef.current) clearTimeout(flyoutCloseTimerRef.current); };
  }, []);
  // ── End Hover flyout ───────────────────────────────────────────────────────

  // Check if all collapsible sections are expanded
  const allExpanded = settingsOpen && aiCoachOpen && mindOpen && achieveOpen && reviewOpen && organizeOpen && mediaOpen && healthOpen && crmOpen;
  
  // Toggle all collapsible sections at once
  const handleToggleAll = () => {
    const newState = !allExpanded;
    setSettingsOpen(newState);
    setAiCoachOpen(newState);
    setMindOpen(newState);
    setAchieveOpen(newState);
    setReviewOpen(newState);
    setOrganizeOpen(newState);
    setMediaOpen(newState);
    setHealthOpen(newState);
    setCrmOpen(newState);
    
    // Persist all states to user-scoped localStorage
    localStorage.setItem(userLsKey('sidebar-settings-open', uid), String(newState));
    localStorage.setItem(userLsKey('sidebar-aicoach-open', uid), String(newState));
    localStorage.setItem(userLsKey('sidebar-mind-open', uid), String(newState));
    localStorage.setItem(userLsKey('sidebar-achieve-open', uid), String(newState));
    localStorage.setItem(userLsKey('sidebar-review-open', uid), String(newState));
    localStorage.setItem(userLsKey('sidebar-organize-open', uid), String(newState));
    localStorage.setItem(userLsKey('sidebar-media-open', uid), String(newState));
    localStorage.setItem(userLsKey('sidebar-health-open', uid), String(newState));
    localStorage.setItem(userLsKey('sidebar-crm-open', uid), String(newState));
  };
  
  const { hasFeature, isLoading: planLoading } = usePlanContext();
  
  // Sidebar collapsed state with shared hook
  const { isCollapsed: sidebarCollapsed, setIsCollapsed: setSidebarCollapsed } = useSidebarCollapsed();

  // Use getQueryFn with returnNull to handle 401s gracefully and prevent crashes
  const { data: resellerConfig } = useQuery<ResellerConfig | null>({
    queryKey: ["/api/reseller/config"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });

  // Check if current user is an assistant (to hide Assistants nav from assistants)
  const { data: assistantProfile } = useQuery<{ id: string } | null>({
    queryKey: ["/api/assistants/profile"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });
  const isAssistant = !!assistantProfile;
  const { data: effectivePermissions } = useQuery<{ permissions: Partial<Record<AssistantCapability, boolean>> } | null>({
    queryKey: ["/api/assistants/effective-permissions"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && isAssistant,
  });

  // Set of nav item IDs an assistant can access, including parent sections that
  // have at least one accessible child. null when the user is not an assistant.
  const assistantEnabledIds = useMemo<Set<string> | null>(() => {
    if (!isAssistant) return null;
    // While permissions are still loading (effectivePermissions is undefined),
    // fail closed — only dashboard is universally accessible until we know
    // which capabilities the manager has granted.
    if (effectivePermissions === undefined) {
      return new Set(["dashboard"]);
    }
    const perms = effectivePermissions?.permissions ?? {};
    const enabled = new Set<string>();
    // Dashboard is universally accessible — no capability required.
    enabled.add("dashboard");
    // Build a parent-id lookup for propagating access up to section headers.
    const parentOf: Record<string, string | undefined> = {};
    menuItemDefinitions.forEach(d => { if (d.parentId) parentOf[d.id] = d.parentId; });
    // Enable leaf items that pass assistantCanAccessMenuItem and propagate
    // that access up to their parent section(s) so the section header turns
    // green too.  Unmapped items (Communicate, Affirmations, etc.) stay gray.
    menuItemDefinitions.forEach(d => {
      if (assistantCanAccessMenuItem(d.id, perms)) {
        enabled.add(d.id);
        // Also enable every ancestor section so the hierarchy stays consistent.
        let pid = parentOf[d.id];
        while (pid) { enabled.add(pid); pid = parentOf[pid]; }
      }
    });
    return enabled;
  }, [isAssistant, effectivePermissions]);

  const { data: userPreferences } = useQuery<{ hiddenMenuItems?: string[] }>({
    queryKey: ["/api/user/preferences"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });
  const hiddenMenuItems: string[] = (userPreferences?.hiddenMenuItems as string[]) || [];

  // Map navigation IDs to feature registry keys
  const featureKeyMap: Record<string, string> = {
    "dashboard": "dashboard",
    "my-goals": "goals",
    "habits": "habits",
    "tasks": "tasks",
    "planner": "planner",
    "journal": "journal",
    "notepad": "notepad",
    "intentions": "intentions",
    "gratefuls": "gratefuls",
    "checklists": "checklists",
    "media": "media",
    "drive": "drive",
    "community": "community",
    "calendar": "calendar",
    "meditation": "meditation",
    "analytics": "analytics",
    "achievements": "achievements",
    "tours": "tours",
    "assistants": "assistants",
    "media-library": "media",
    "google-drive": "drive",
    "zeus": "zeus",
    "vboard": "vboard",
    "trackerx": "trackerx",
    "health": "health",
    "meal-planner": "meal_planner",
    "workout-planner": "workout_planner",
    "health-metrics": "health_metrics",
    "food-goal": "food_goal",
    "fitness-goal": "fitness_goal",
    "ai-coach-start": "ai_coach",
    "ai-coach-ask": "ask_ai",
    "goal-simulation": "goal_simulation",
    "ai-create": "ai_create",
    "time-budget": "time_budget",
    "settings-main": "preferences",
    "sops": "sops",
    "zeus-settings": "zeus_settings",
    "vault": "vault",
    "billing": "billing",
    "api-keys": "api_keys",
    "suggestions": "suggestions",
    "testimonials": "testimonials",
    "contacts": "contacts",
    ...menuItemFeatureKeyOverrides,
  };

  const defaultNavigation: NavigationItem[] = menuItemDefinitions.map(item => ({
    id: item.id,
    name: item.name,
    href: item.href,
    icon: item.icon,
    current: item.currentMatch ? item.currentMatch(location) : false,
    isCollapsible: item.isCollapsible,
    parentId: item.parentId || null,
    isStaticChild: item.isStaticChild,
  }));

  // Filter navigation based on plan access and custom menu visibility
  const filteredNavigation = defaultNavigation
    .filter(item => {
      // Hide items the user has toggled off in Custom Menu settings
      if (hiddenMenuItems.includes(item.id)) return false;
      // Hide child items when their parent section is hidden
      if (item.parentId && hiddenMenuItems.includes(item.parentId)) return false;

      const featureKey = featureKeyMap[item.id];
      // Master Admin section and its children are only visible to the platform owner
      if ((item.id === "master-admin" || item.parentId === "master-admin") && !isMasterAdmin) return false;
      if (
        item.id === "platform-operations" &&
        !hasPermission("tenant.provision", "user.invite", "plan.assign")
      ) return false;
      if (
        item.id === "platform-billing" &&
        !hasPermission("billing.view", "billing.manage")
      ) return false;
      if (item.id === "platform-roles" && !isPlatformOwner) return false;
      // Hide assistant roster menus from assistant users (they can't manage these relationships)
      if ((item.id === "assistants" || item.id === "coach") && isAssistant) return false;
      // For assistant users keep every item visible — enabled ones are dark green,
      // disabled ones are grayed-out and non-clickable (see SortableNavItem).
      if (isAssistant) return true;
      // Always show settings and collapsible sections (unless user explicitly hid them)
      if (item.id === "settings" || item.isCollapsible) return true;
      // Static children are gated by their OWN feature key (if mapped) and
      // additionally hidden when their parent's feature is off.
      if (item.isStaticChild) {
        if (planLoading) return true;
        if (featureKey && !hasFeature(featureKey as any)) return false;
        const parentFeatureKey = featureKeyMap[item.parentId || ''];
        if (parentFeatureKey && !hasFeature(parentFeatureKey as any)) return false;
        return true;
      }
      // If no feature key mapping or plan loading, show item by default
      if (!featureKey || planLoading) return true;
      // Check plan access
      return hasFeature(featureKey as any);
    });

  const { items: navigation, reorderItems, setItemParent, moveNode } = useNavigationOrder(filteredNavigation, isAuthenticated);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setNestTargetId(null);
    setDropPosition(null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over, delta } = event;
    
    if (!over || active.id === over.id) {
      setNestTargetId(null);
      setDropPosition(null);
      return;
    }

    const overItem = navigation.find(item => item.id === over.id);
    if (!overItem) {
      setNestTargetId(null);
      setDropPosition(null);
      return;
    }

    // Allow nesting under collapsible items - they now support dynamic children

    const overRect = event.over?.rect;
    const activeInitialRect = active.rect.current.initial;
    
    if (!overRect || !activeInitialRect) {
      setNestTargetId(null);
      setDropPosition('before');
      return;
    }
    
    // Calculate pointer position using multiple methods for reliability
    // Method 1: Use tracked mouse position (works with real mouse events)
    let pointerX = mousePositionRef.current.x;
    let pointerY = mousePositionRef.current.y;
    
    // Method 2: If mouse position is 0,0 (not tracked), calculate from initial rect + delta
    if (pointerX === 0 && pointerY === 0) {
      pointerX = activeInitialRect.left + (activeInitialRect.width / 2) + delta.x;
      pointerY = activeInitialRect.top + (activeInitialRect.height / 2) + delta.y;
    }
    
    // Check if we're in the "nest zone" (right 40% of the item)
    const nestThreshold = overRect.left + overRect.width * 0.6;
    
    // Determine if the target can accept children (must be collapsible or a static child of a collapsible)
    let canAcceptChildren = overItem.isCollapsible;
    let actualNestParentId = over.id as string;
    
    if (overItem.isStaticChild && overItem.parentId) {
      // If dropping on a static child, nest under its parent
      const parentItem = navigation.find(item => item.id === overItem.parentId);
      canAcceptChildren = parentItem?.isCollapsible || false;
      actualNestParentId = overItem.parentId;
    }
    
    if (pointerX > nestThreshold && canAcceptChildren) {
      // In the right 40% of a collapsible item - nest mode
      setNestTargetId(actualNestParentId);
      setDropPosition('nest');
    } else {
      setNestTargetId(null);
      // Determine if before or after based on Y position
      // Also consider the original position - if dragging from below to above, prefer 'before'
      const activeIndex = navigation.findIndex(item => item.id === active.id);
      const overIndex = navigation.findIndex(item => item.id === over.id);
      const isDraggingUp = activeIndex > overIndex;
      
      // When dragging up (item is below target in list), user wants to place before
      // When dragging down (item is above target in list), user wants to place after
      // This is more intuitive than pointer position for most drag operations
      const position = isDraggingUp ? 'before' : 'after';
      setDropPosition(position);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const activeItem = navigation.find(item => item.id === active.id);
      const overItem = navigation.find(item => item.id === over.id);
      
      if (activeItem && overItem) {
        // Use the tree-aware moveNode for all operations
        if (dropPosition === 'nest' && nestTargetId) {
          // Nest inside the target (or its parent if target is a static child)
          moveNode(active.id as string, nestTargetId, 'inside');
        } else if (dropPosition === 'before') {
          moveNode(active.id as string, over.id as string, 'before');
        } else if (dropPosition === 'after') {
          moveNode(active.id as string, over.id as string, 'after');
        }
      }
    }
    
    setActiveId(null);
    setNestTargetId(null);
    setDropPosition(null);
  };

  const activeItem = activeId ? navigation.find((item) => item.id === activeId) : null;

  // Flyout panel content — children of the currently hovered section
  const flyoutChildren = flyoutSection
    ? navigation.filter(n => n.parentId === flyoutSection)
    : [];
  const flyoutSectionItem = flyoutSection
    ? navigation.find(n => n.id === flyoutSection)
    : null;

  return (
    <TooltipProvider delayDuration={0}>
      <>
      <aside 
        className={`hidden lg:flex shrink-0 flex-col fixed inset-y-0 bg-card border-r border-border z-10 transition-all duration-300 ${
          sidebarCollapsed ? 'w-[72px]' : 'w-64'
        }`}
      >
        <div className="flex flex-col flex-grow pt-6 pb-4 overflow-hidden">
          {/* Logo and Collapse Toggle */}
          <div className={`flex flex-shrink-0 ${sidebarCollapsed ? 'items-center flex-col gap-2' : 'flex-col gap-1'}`}>
            {sidebarCollapsed ? (
              <>
                {/* Brand mark + quick-create on same row */}
                <div className="flex items-center justify-between w-full px-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-pointer select-none">
                        <span className="text-base font-bold leading-none">
                          <span className="text-foreground">G</span><span className="text-orange-500">V</span>
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {resellerConfig?.companyName || "GoalVia"}
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          className="w-8 h-8 bg-primary hover:bg-primary/90 text-primary-foreground"
                          data-testid="button-quick-create-collapsed"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Quick Create
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent side="right" align="start" className="w-48">
                    <Link href="/goals?quickCreate=goal">
                      <DropdownMenuItem className="cursor-pointer">
                        <Target className="w-4 h-4 mr-2 text-primary" />
                        New Goal
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/tasks?quickCreate=task">
                      <DropdownMenuItem className="cursor-pointer">
                        <ListTodo className="w-4 h-4 mr-2 text-purple-500" />
                        New Task
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/habits?quickCreate=habit">
                      <DropdownMenuItem className="cursor-pointer">
                        <CheckSquare className="w-4 h-4 mr-2 text-green-500" />
                        New Habit
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/notepad?quickCreate=note">
                      <DropdownMenuItem className="cursor-pointer">
                        <StickyNote className="w-4 h-4 mr-2 text-yellow-500" />
                        New Note
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/calendar?quickCreate=event">
                      <DropdownMenuItem className="cursor-pointer">
                        <CalendarPlus className="w-4 h-4 mr-2 text-blue-500" />
                        New Event
                      </DropdownMenuItem>
                    </Link>
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
                <NotificationBell location="collapsed" />
                <TimeClockButton />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link href="/settings">
                      <Button
                        variant={location === "/settings" ? "default" : "ghost"}
                        size="icon"
                        className={`w-10 h-10 ${
                          location === "/settings"
                            ? "bg-primary text-primary-foreground" 
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                        data-testid="link-admin-panel-header"
                      >
                        <Settings className="w-5 h-5" />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    Settings
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSidebarCollapsed(false)}
                      className="w-10 h-10 text-muted-foreground hover:text-foreground hover:bg-muted"
                      data-testid="button-expand-sidebar"
                    >
                      <PanelLeft className="w-5 h-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    Expand Sidebar
                  </TooltipContent>
                </Tooltip>
              </>
            ) : (
              <>
                {/* Row 1: Brand */}
                <div className="flex items-center justify-center px-4">
                  {resellerConfig?.companyName && resellerConfig.companyName !== "GoalVia" ? (
                    <span className="text-xl font-bold text-foreground" data-testid="text-app-name">
                      {resellerConfig.companyName}
                    </span>
                  ) : (
                    <img
                      src={goalViaLogo}
                      alt="GoalVia"
                      className="w-[70%] h-auto object-contain"
                      data-testid="text-app-name"
                    />
                  )}
                </div>
                {/* Row 2: Action icon buttons */}
                <div className="flex items-center gap-1 px-3">
                  <TimeClockButton />
                  <DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            className="w-8 h-8 bg-primary hover:bg-primary/90 text-primary-foreground"
                            data-testid="button-quick-create"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent>Quick Create</TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="w-48">
                      <Link href="/goals?quickCreate=goal">
                        <DropdownMenuItem className="cursor-pointer">
                          <Target className="w-4 h-4 mr-2 text-primary" />
                          New Goal
                        </DropdownMenuItem>
                      </Link>
                      <Link href="/tasks?quickCreate=task">
                        <DropdownMenuItem className="cursor-pointer">
                          <ListTodo className="w-4 h-4 mr-2 text-purple-500" />
                          New Task
                        </DropdownMenuItem>
                      </Link>
                      <Link href="/habits?quickCreate=habit">
                        <DropdownMenuItem className="cursor-pointer">
                          <CheckSquare className="w-4 h-4 mr-2 text-green-500" />
                          New Habit
                        </DropdownMenuItem>
                      </Link>
                      <Link href="/notepad?quickCreate=note">
                        <DropdownMenuItem className="cursor-pointer">
                          <StickyNote className="w-4 h-4 mr-2 text-yellow-500" />
                          New Note
                        </DropdownMenuItem>
                      </Link>
                      <Link href="/calendar?quickCreate=event">
                        <DropdownMenuItem className="cursor-pointer">
                          <CalendarPlus className="w-4 h-4 mr-2 text-blue-500" />
                          New Event
                        </DropdownMenuItem>
                      </Link>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <MessageBadge />
                  <NotificationBell location="sidebar" />
                  <TourLauncher variant="icon" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link href="/settings">
                        <Button
                          variant={location === "/settings" ? "default" : "ghost"}
                          size="icon"
                          className={`w-8 h-8 ${
                            location === "/settings"
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground hover:bg-muted"
                          }`}
                          data-testid="link-admin-panel-header"
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Settings</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSidebarCollapsed(true)}
                        className="w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                        data-testid="button-collapse-sidebar"
                      >
                        <PanelLeftClose className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Collapse Sidebar</TooltipContent>
                  </Tooltip>
                </div>
              </>
            )}
          </div>

          {/* Navigation with Drag and Drop */}
          <nav className="mt-8 flex-1 basis-0 min-h-0 flex flex-col overflow-hidden">
            {/* Expand/Collapse All Button */}
            <div className={`mb-2 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
              {sidebarCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleToggleAll}
                      className="w-full text-muted-foreground hover:text-foreground hover:bg-muted"
                      data-testid="button-toggle-all-sections"
                    >
                      {allExpanded ? (
                        <ChevronsDownUp className="w-4 h-4" />
                      ) : (
                        <ChevronsUpDown className="w-4 h-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {allExpanded ? "Collapse All" : "Expand All"}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleAll}
                  className="w-full justify-start text-xs text-muted-foreground hover:text-foreground hover:bg-muted"
                  data-testid="button-toggle-all-sections"
                >
                  {allExpanded ? (
                    <>
                      <ChevronsDownUp className="w-3 h-3 mr-2" />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <ChevronsUpDown className="w-3 h-3 mr-2" />
                      Expand All
                    </>
                  )}
                </Button>
              )}
            </div>
            
            <div className={`space-y-1 flex-grow overflow-y-auto min-h-0 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={navigation.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {navigation.map((item) => {
                    const collapsibleStates: Record<string, boolean> = {
                      "settings": settingsOpen,
                      "ai-coach": aiCoachOpen,
                      "mind": mindOpen,
                      "achieve": achieveOpen,
                      "review": reviewOpen,
                      "organize": organizeOpen,
                      "media": mediaOpen,
                      "health": healthOpen,
                      "crm": crmOpen,
                    };
                    
                    const toggleHandlers: Record<string, () => void> = {
                      "settings": () => handleSettingsToggle(!settingsOpen),
                      "ai-coach": () => handleAiCoachToggle(!aiCoachOpen),
                      "mind": () => handleMindToggle(!mindOpen),
                      "achieve": () => handleAchieveToggle(!achieveOpen),
                      "review": () => handleReviewToggle(!reviewOpen),
                      "organize": () => handleOrganizeToggle(!organizeOpen),
                      "media": () => handleMediaToggle(!mediaOpen),
                      "health": () => handleHealthToggle(!healthOpen),
                      "crm": () => handleCrmToggle(!crmOpen),
                    };
                    
                    // Calculate if item should be hidden — traverse full parent chain
                    let isHidden = false;
                    let checkId: string | null | undefined = item.parentId;
                    while (checkId) {
                      const parentItem = navigation.find(n => n.id === checkId);
                      if (parentItem?.isCollapsible) {
                        const parentIsOpen = collapsibleStates[checkId] ?? false;
                        if (!parentIsOpen) { isHidden = true; break; }
                      }
                      checkId = parentItem?.parentId;
                    }
                    
                    const assistantAccessible = assistantEnabledIds === null
                      ? undefined
                      : assistantEnabledIds.has(item.id) ? true : false;

                    return (
                      <SortableNavItem 
                        key={item.id} 
                        item={item}
                        isOpen={collapsibleStates[item.id]}
                        onToggle={toggleHandlers[item.id]}
                        sidebarCollapsed={sidebarCollapsed}
                        isNestTarget={nestTargetId === item.id}
                        isHidden={isHidden}
                        onHoverEnter={handleSectionHoverEnter}
                        onHoverLeave={handleSectionHoverLeave}
                        assistantAccessible={assistantAccessible}
                      />
                    );
                  })}
                </SortableContext>
                <DragOverlay>
                  {activeItem ? (
                    <div className="bg-card border border-border rounded-md shadow-lg">
                      <Button
                        variant="default"
                        className={`${sidebarCollapsed ? 'w-12' : 'w-64'} justify-start bg-primary text-primary-foreground cursor-grabbing`}
                      >
                        <activeItem.icon className="w-4 h-4 mr-3" />
                        {!sidebarCollapsed && activeItem.name}
                      </Button>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>

          </nav>

          {/* Calendar source filter panel – visible only on /calendar, pinned above collapse button */}
          <CalendarSidebarPanel sidebarCollapsed={sidebarCollapsed} isAuthenticated={isAuthenticated} />

          {/* Collapse Toggle Button */}
          <div className={`flex-shrink-0 border-t border-border ${sidebarCollapsed ? 'px-2' : 'px-3'} py-2`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size={sidebarCollapsed ? "icon" : "sm"}
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className={`${sidebarCollapsed ? 'w-full' : 'w-full justify-start'} text-muted-foreground hover:text-foreground hover:bg-muted`}
                  data-testid="button-toggle-sidebar"
                >
                  {sidebarCollapsed ? (
                    <PanelLeft className="w-5 h-5" />
                  ) : (
                    <>
                      <PanelLeftClose className="w-4 h-4 mr-3" />
                      Collapse
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              </TooltipContent>
            </Tooltip>
          </div>

          {/* User Profile */}
          <div className="flex-shrink-0 border-t border-border">
            <div className={sidebarCollapsed ? 'p-2' : 'p-4'}>
              {sidebarCollapsed ? (
                <div className="flex flex-col items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Avatar className="w-10 h-10 cursor-pointer">
                        <AvatarImage src={(user as any)?.profileImageUrl || undefined} />
                        <AvatarFallback>
                          {user?.firstName?.[0]}{user?.lastName?.[0] || "U"}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{user?.firstName} {user?.lastName}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="w-full"
                        onClick={() => { clearUserScopedStorage(user?.id); window.location.href = "/api/logout"; }}
                        data-testid="button-logout"
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Logout
                    </TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <div className="flex items-center w-full">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={(user as any)?.profileImageUrl || undefined} />
                    <AvatarFallback>
                      {user?.firstName?.[0]}{user?.lastName?.[0] || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="ml-2 flex-shrink-0" 
                    onClick={() => { clearUserScopedStorage(user?.id); window.location.href = "/api/logout"; }}
                    data-testid="button-logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Flyout panel — pops out to the right of the sidebar on hover */}
      {flyoutSection && flyoutChildren.length > 0 && (
        <div
          className="fixed z-50 bg-card border border-border rounded-lg shadow-xl py-1 w-52 overflow-y-auto"
          style={{
            left: sidebarCollapsed ? 72 : 256,
            top: flyoutY,
            maxHeight: `calc(100vh - ${flyoutY}px - 16px)`,
          }}
          onMouseEnter={handleFlyoutMouseEnter}
          onMouseLeave={handleFlyoutMouseLeave}
        >
          {flyoutSectionItem && (
            <div className="px-3 py-2 border-b border-border mb-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {flyoutSectionItem.name}
              </p>
            </div>
          )}
          {flyoutChildren.map(child => {
            const ChildIcon = child.icon;
            return (
              <Link key={child.id} href={child.href} onClick={() => setFlyoutSection(null)}>
                <div className={`flex items-center px-3 py-1.5 mx-1 rounded-md text-sm cursor-pointer transition-colors ${
                  child.current
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                }`}>
                  <ChildIcon className="w-4 h-4 mr-2.5 flex-shrink-0" />
                  <span className="truncate">{child.name}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      </>
    </TooltipProvider>
  );
}
